import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, PendingMfaState } from '../../../services/auth.service';

type MfaMode = 'verify' | 'enroll' | 'recovery';

@Component({
  selector: 'app-mfa',
  standalone: false,
  templateUrl: './mfa.page.html',
  styleUrl: './mfa.page.css',
})
export class MfaPage implements OnInit {
  mode: MfaMode = 'verify';
  totpCode = '';
  recoveryCode = '';
  loading = false;
  errorMessage = '';
  successMessage = '';

  copiedSecret = false;
  copiedRecoveryCodes = false;
  hasSavedRecoveryCodes = false;

  qrCodeDataUrl = '';
  secret = '';
  recoveryCodes: string[] = [];
  email = '';
  pendingState: PendingMfaState | null = null;
  returnUrl = '/dashboard/home';

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    const rawReturnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    this.returnUrl = this.sanitizeReturnUrl(rawReturnUrl);

    this.pendingState = this.authService.getPendingMfaState();

    // If already logged in, 2FA is enrolled, and no pending MFA challenge exists, proceed to dashboard
    if (this.authService.isLoggedIn() && this.authService.isMfaEnrolled() && !this.pendingState) {
      this.router.navigateByUrl(this.returnUrl);
      return;
    }

    // If neither pending state nor logged in session exists, redirect to login
    if (!this.pendingState && !this.authService.isLoggedIn()) {
      this.router.navigate(['/auth/login']);
      return;
    }

    // If user has a session but pending state was cleared (e.g. page refresh), reconstruct from member
    if (!this.pendingState && this.authService.isLoggedIn()) {
      const member: any = this.authService.getMember()?.getSync() || (this.authService as any).stytch?.self?.getSync();
      const isEnrolled = this.authService.isMfaEnrolled();
      this.pendingState = {
        memberId: member?.member_id || '',
        organizationId: member?.organization_id || '',
        email: member?.email_address || '',
        isEnrolled: isEnrolled,
      };
      this.authService.setPendingMfaState(this.pendingState);
    }

    if (this.pendingState) {
      this.email = this.pendingState.email || '';
      if (this.pendingState.isEnrolled) {
        this.mode = 'verify';
      } else {
        this.mode = 'enroll';
        this.initEnrollment();
      }
    }
  }

  async initEnrollment() {
    this.loading = true;
    this.errorMessage = '';
    try {
      const res = await this.authService.createTOTP();
      if (res.qr_code) {
        if (res.qr_code.startsWith('data:')) {
          this.qrCodeDataUrl = res.qr_code;
        } else if (res.qr_code.startsWith('<svg') || res.qr_code.includes('<svg')) {
          this.qrCodeDataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(res.qr_code)}`;
        } else {
          this.qrCodeDataUrl = `data:image/svg+xml;base64,${res.qr_code}`;
        }
      }
      this.secret = res.secret || '';
      this.recoveryCodes = res.recovery_codes || [];
    } catch (err: any) {
      console.error('Failed to initialize TOTP enrollment:', err);
      this.errorMessage =
        err?.error_message ||
        err?.message ||
        'Failed to set up 2FA. Please try again or contact support.';
    } finally {
      this.loading = false;
    }
  }

  setMode(mode: MfaMode) {
    this.mode = mode;
    this.errorMessage = '';
    this.totpCode = '';
    this.recoveryCode = '';
  }

  copySecret() {
    if (!this.secret) return;
    navigator.clipboard.writeText(this.secret).then(() => {
      this.copiedSecret = true;
      setTimeout(() => (this.copiedSecret = false), 2000);
    });
  }

  copyRecoveryCodes() {
    if (!this.recoveryCodes.length) return;
    navigator.clipboard.writeText(this.recoveryCodes.join('\n')).then(() => {
      this.copiedRecoveryCodes = true;
      setTimeout(() => (this.copiedRecoveryCodes = false), 2000);
    });
  }

  downloadRecoveryCodes() {
    if (!this.recoveryCodes.length) return;
    const content = [
      '=====================================================',
      'MONECO DASHBOARD - EMERGENCY MFA RECOVERY CODES',
      '=====================================================',
      `Generated: ${new Date().toISOString()}`,
      `Account: ${this.email || 'Moneco Dashboard Member'}`,
      '',
      'IMPORTANT SECURITY WARNING:',
      '- Store these codes in a secure, confidential place (e.g. password manager).',
      '- Each recovery code can only be used ONCE to sign in if you lose your phone.',
      '- Do NOT share these codes with anyone.',
      '',
      'RECOVERY CODES:',
      ...this.recoveryCodes.map((code, idx) => `  ${idx + 1}. ${code}`),
      '=====================================================',
    ].join('\n');

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `moneco-dashboard-recovery-codes-${new Date().toISOString().slice(0, 10)}.txt`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  onTotpCodeChange() {
    if (this.loading || !/^\d{6}$/.test(this.totpCode.trim())) return;
    if (this.mode === 'enroll' && !this.hasSavedRecoveryCodes) return;
    this.submitTotp();
  }

  async submitTotp() {
    this.errorMessage = '';
    const cleanCode = this.totpCode.trim();

    if (!cleanCode) {
      this.errorMessage = 'Please enter the 6-digit verification code.';
      return;
    }

    if (!/^\d{6}$/.test(cleanCode)) {
      this.errorMessage = 'The verification code must be exactly 6 digits.';
      return;
    }

    this.loading = true;
    try {
      if (this.mode === 'enroll') {
        await this.authService.authenticateTOTP(cleanCode, {
          enroll: true,
          setDefaultMfa: true,
        });
      } else {
        await this.authService.authenticateTOTP(cleanCode);
      }

      this.authService.clearPendingMfaState();
      this.router.navigateByUrl(this.returnUrl);
    } catch (err: any) {
      console.error('TOTP authentication error:', err);
      this.errorMessage =
        err?.error_message ||
        err?.message ||
        'Invalid authentication code. Please check your authenticator app and try again.';
    } finally {
      this.loading = false;
    }
  }

  async submitRecoveryCode() {
    this.errorMessage = '';
    const cleanCode = this.recoveryCode.trim();

    if (!cleanCode) {
      this.errorMessage = 'Please enter your recovery code.';
      return;
    }

    this.loading = true;
    try {
      await this.authService.recoverWithCode(cleanCode);
      this.authService.clearPendingMfaState();
      this.router.navigateByUrl(this.returnUrl);
    } catch (err: any) {
      console.error('Recovery code error:', err);
      this.errorMessage =
        err?.error_message ||
        err?.message ||
        'Invalid recovery code. Please check and try again.';
    } finally {
      this.loading = false;
    }
  }

  private sanitizeReturnUrl(url: string | null): string {
    if (!url) return '/dashboard/home';
    if (url.startsWith('/dashboard')) {
      return url;
    }
    return '/dashboard/home';
  }

  async backToLogin() {
    this.authService.clearPendingMfaState();
    try {
      await this.authService.logout();
    } catch (e) {
      // ignore
    }
    this.router.navigate(['/auth/login']);
  }
}
